import { canonicalAttributePath } from './attribute-paths';

/**
 * The ONE parser for product-listing facet configuration, shared by both listing paths:
 *
 *   - V2 CMS listing block (server path): `section_with_product_listing.facets_configuration`, a JSON
 *     string. Its keys are canonicalised (`searchableAttributes.*` → `attributes.*`, see
 *     canonicalAttributePath) because the in-memory engine reads the canonical copy.
 *   - `/search` (browser path): the Frontastic Studio `facetsConfiguration` / `pricesConfiguration`
 *     arrays. Keys stay raw, because there they go to Algolia, which only knows the
 *     `searchableAttributes.*` copy.
 *
 * It returns exactly the configured entries in the configured order and drops nothing: the facet panel
 * renders in this order, and its Clark/Dorf "first 8 facets until something is picked" rule
 * (hooks/useDynamicFacets) counts these positions. Hiding a facet (no values, other market) is the
 * panel's job, not this parser's.
 *
 * The value shapes match FacetConfiguration / PriceConfiguration in the listing organisms' `types`.
 */

export type FacetType = 'range' | 'color' | 'term' | 'boolean';

/** One configured facet. `rangeValues` are inline range buckets (CMS `range` facets only). */
export interface FacetConfig {
  key: string;
  type: FacetType;
  label: string;
  rangeValues?: string[];
}

export interface PriceRange {
  min: number;
  max: number;
  refinements?: number;
}

export interface PriceConfig {
  key: string;
  ranges: PriceRange[];
}

interface ResolveOptions {
  /** Substituted for `{currency}` in facet keys (e.g. `variants.prices.{currency}…`). */
  currency: string;
  /** True on the server path: rewrite keys onto the canonical `attributes.*` copy. */
  canonical: boolean;
}

const isString = (v: unknown): v is string => typeof v === 'string';

/** Inline range buckets from a CMS facet entry, tolerating the three spellings authors have used. */
const rangeValuesOf = (item: Record<string, unknown>): string[] | undefined => {
  const raw = item.rangeValues ?? item['range values'] ?? item.range_values;
  if (!Array.isArray(raw)) return undefined;
  const values = raw.filter((v): v is string => isString(v) && v.trim().length > 0);
  return values.length > 0 ? values : undefined;
};

/**
 * Facet entries from either source: a CMS JSON string or a Studio array. Entries without a string
 * `key`, `type` and `label` are skipped (they cannot be rendered); malformed JSON yields `[]`.
 */
export const parseFacetConfigs = (raw: unknown): FacetConfig[] => {
  let source: unknown = raw;
  if (isString(raw)) {
    if (!raw.trim()) return [];
    try {
      source = JSON.parse(raw.trim());
    } catch {
      return [];
    }
  }
  if (!Array.isArray(source)) return [];

  const out: FacetConfig[] = [];
  for (const item of source) {
    if (!item || typeof item !== 'object') continue;
    const { key, type, label } = item as Record<string, unknown>;
    if (!isString(key) || !isString(type) || !isString(label)) continue;
    const rangeValues = rangeValuesOf(item as Record<string, unknown>);
    out.push({ key, type: type as FacetType, label, ...(rangeValues ? { rangeValues } : {}) });
  }
  return out;
};

const resolveKey = (key: string, { currency, canonical }: ResolveOptions): string => {
  const withCurrency = key.replace(/\{currency\}/, currency);
  return canonical ? canonicalAttributePath(withCurrency) : withCurrency;
};

/** `{ attribute: { key, type, label } }` in configured order — what the facet panel renders. */
export const facetsByAttribute = (facets: FacetConfig[], options: ResolveOptions): Record<string, FacetConfig> =>
  facets.reduce<Record<string, FacetConfig>>((acc, facet) => {
    const key = resolveKey(facet.key, options);
    acc[key] = { key, type: facet.type, label: facet.label };
    return acc;
  }, {});

/** Parses `["0-180", "181-220"]` into `[{ min: 0, max: 180 }, { min: 181, max: 220 }]`. */
const parseRangeValues = (values: string[]): PriceRange[] =>
  values
    .map((value) => {
      const [min, max] = String(value)
        .split('-')
        .map((part) => Number(part.trim()));
      return { min, max };
    })
    .filter(({ min, max }) => Number.isFinite(min) && Number.isFinite(max));

/**
 * `{ attribute: { key, ranges } }` for range facets: Studio `pricesConfiguration` entries, plus the
 * inline `rangeValues` buckets of CMS `range` facets.
 */
export const pricesByAttribute = (
  facets: FacetConfig[],
  options: ResolveOptions,
  studioPrices: Array<{ key: string; ranges: PriceRange[] }> = [],
): Record<string, PriceConfig> => {
  const acc: Record<string, PriceConfig> = {};
  for (const price of studioPrices) {
    const key = options.canonical ? canonicalAttributePath(price.key) : price.key;
    acc[key] = { key, ranges: price.ranges };
  }
  for (const facet of facets) {
    if (facet.type !== 'range' || !facet.rangeValues?.length) continue;
    const ranges = parseRangeValues(facet.rangeValues);
    if (ranges.length === 0) continue;
    const key = resolveKey(facet.key, options);
    acc[key] = { key, ranges };
  }
  return acc;
};

/**
 * The canonical facet attribute paths of a CMS block, in configured order and deduped: the facet list
 * the in-memory engine counts AND the extra fields the server preload retrieves, so a facet is only ever
 * counted against a path that was actually fetched.
 */
export const facetAttributePaths = (facets: FacetConfig[], currency: string): string[] =>
  Object.keys(facetsByAttribute(facets, { currency, canonical: true }));
